import React, { useState, useRef, useEffect } from "react";
import clsx from "clsx";
import { ChevronDown, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight, Check } from "lucide-react";
import { styles } from "./styles";

interface PaginationConfig {
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onSizeChange: (size: number) => void;
}

interface PaginationProps {
  pagination: PaginationConfig;
}

const Pagination: React.FC<PaginationProps> = ({ pagination }) => {
  const { page, size, totalElements, totalPages, onPageChange, onSizeChange } = pagination;

  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const sizes = [1, 5, 10, 20, 50];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const start = totalElements === 0 ? 0 : page * size + 1;
  const end = Math.min((page + 1) * size, totalElements);

  return (
    <div className={styles.container}>
      <div className={styles.left}>
        {start} – {end} of {totalElements}
      </div>

      <div className={styles.right}>
        <div className={styles.sizeWrapper} ref={dropdownRef}>
          <button
            onClick={() => setOpen(!open)}
            className={styles.sizeButton}
          >
            {size}
            <ChevronDown className="w-4 h-5 ml-1 text-gray-600" />
          </button>

          {open && (
            <div className={styles.sizeList}>
              {sizes.map((s) => (
                <div
                  key={s}
                  onClick={() => {
                    onSizeChange(s);
                    setOpen(false);
                  }}
                  className={clsx(styles.sizeItem, s === size && styles.sizeItemActive)}
                >
                  {s === size && <Check className="w-4 h-4 text-blue-600" />}
                  <span>{s}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.navWrapper}>
          <PageBtn disabled={page === 0} label={<ChevronsLeft className="w-4 h-5 text-black" />} onClick={() => onPageChange(0)} />
          <PageBtn disabled={page === 0} label={<ChevronLeft className="w-4 h-5 text-black" />} onClick={() => onPageChange(page - 1)} />

          <span className="mx-1">
            {page + 1} / {totalPages}
          </span>

          <PageBtn disabled={page >= totalPages - 1} label={<ChevronRight className="w-4 h-5 text-black" />} onClick={() => onPageChange(page + 1)} />
          <PageBtn disabled={page >= totalPages - 1} label={<ChevronsRight className="w-4 h-5 text-black" />} onClick={() => onPageChange(totalPages - 1)} />
        </div>
      </div>
    </div>
  );
};

interface BtnProps {
  disabled: boolean;
  label: React.ReactNode;
  onClick: () => void;
}

const PageBtn: React.FC<BtnProps> = ({ disabled, label, onClick }) => (
  <button
    disabled={disabled}
    onClick={onClick}
    className={clsx(styles.navBtn, disabled && styles.navDisabled)}
  >
    {label}
  </button>
);

export default Pagination;
